import { getCurrentToken } from '@/features/auth';
import { handleTokenRefreshFailure, refreshAccessToken } from '@/features/auth/utils/tokenRefresh';
import i18n from '@/shared/lib/i18n';
import logger from '@/shared/lib/logger';

// Centralized BASE_URL - export for use in streaming services
export const BASE_URL = (import.meta.env.VITE_BASE_URL || 'http://localhost:8888') + '/api/v1';

/** Timeout padrão para uploads (multipart e SSE de ingestão). */
const UPLOAD_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutos
const UPLOAD_STREAM_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutos

interface RequestOptions extends RequestInit {
    requiresAuth?: boolean;
    isEventStream?: boolean;
    onProgress?: (data: unknown) => void;
    timeout?: number;
    _isRetry?: boolean;
    responseType?: 'json' | 'text' | 'blob';
}

interface StreamEventPayload {
    event?: string;
    message?: string;
    result?: unknown;
}

function asStreamEventPayload(value: unknown): StreamEventPayload {
    if (typeof value !== 'object' || value === null) return {};
    return value as StreamEventPayload;
}

/** Estratégia proativa: tenta renovar o token antes de qualquer requisição (inclusive SSE) */
export async function getAuthToken(): Promise<string | null> {
    try {
        const freshToken = await refreshAccessToken();
        return freshToken || getCurrentToken();
    } catch (error) {
        logger.error('[HttpClient] Erro ao obter token:', error);
        return getCurrentToken();
    }
}

interface FetchWithAuthRetryOptions {
    /** Best-effort: em falha definitiva de refresh, retorna a resposta 401 em vez de lançar erro/redirecionar. */
    silent?: boolean;
}

/**
 * fetch() com a mesma estratégia de renovação de HttpClient.request() (proativa + 1 retry
 * reativo em 401). Para os endpoints de streaming (SSE/EventSource-like) que não podem passar
 * por HttpClient.request() porque ele consome a resposta inteira antes de retornar.
 */
export async function fetchWithAuthRetry(
    url: string,
    init: RequestInit = {},
    options: FetchWithAuthRetryOptions = {},
    _isRetry = false,
): Promise<Response> {
    const token = await getAuthToken();
    const headers = new Headers(init.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);

    const response = await fetch(url, { ...init, headers });

    if (response.status === 401 && !_isRetry) {
        const newToken = await refreshAccessToken();
        if (newToken) {
            return fetchWithAuthRetry(url, init, options, true);
        }
        if (options.silent) return response;
        await handleTokenRefreshFailure('sse-401-refresh-null');
        throw new Error(i18n.t('auth:sessionExpired'));
    }

    return response;
}

class HttpClient {
    private baseUrl: string;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    /** Estratégia proativa: tenta renovar token antes da requisição */
    private async getAuthHeaders(): Promise<HeadersInit> {
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            'Accept-Language': i18n.language || 'pt-BR',
        };

        const token = await getAuthToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        return headers;
    }

    /** Requisição com refresh automático (proativo + reativo) */
    async request<T>(
        endpoint: string,
        options: RequestOptions = {}
    ): Promise<T> {
        const { requiresAuth = true, headers: customHeaders, _isRetry = false, ...restOptions } = options;

        let headers: HeadersInit = {
            'Content-Type': 'application/json',
            'Accept-Language': i18n.language || 'pt-BR',
            ...customHeaders,
        };

        if (requiresAuth) {
            const authHeaders = await this.getAuthHeaders();
            headers = { ...headers, ...authHeaders };
        }

        // Timeout para requests normais - 5 minutos
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
            controller.abort();
        }, 5 * 60 * 1000); // 5 minutos

        try {
            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                ...restOptions,
                headers,
                signal: controller.signal,
            });

            // Estratégia reativa: trata 401 com retry
            if (response.status === 401 && requiresAuth && !_isRetry) {
                try {
                    const newToken = await refreshAccessToken();

                    if (newToken) {
                        return this.request<T>(endpoint, {
                            ...options,
                            _isRetry: true,
                        });
                    } else {
                        await handleTokenRefreshFailure('http-401-refresh-null');
                        throw new Error(i18n.t('auth:sessionExpired'));
                    }
                } catch (refreshError) {
                    await handleTokenRefreshFailure('http-401-refresh-error');
                    throw new Error(i18n.t('auth:sessionExpiredRenew'));
                }
            }

            if (!response.ok) {
                const errorBody = await response.text();
                if (response.status === 403) {
                    try {
                        const parsed: { detail?: { code?: string; message?: string } } = JSON.parse(errorBody);
                        if (parsed?.detail?.code === 'ROLE_NOT_ALLOWED') {
                            sessionStorage.setItem('auth_error', parsed.detail?.message ?? 'Acesso negado.');
                            window.location.href = '/access-denied';
                            throw new Error(parsed.detail?.message ?? 'Acesso negado. Você não possui permissão para acessar este sistema.');
                        }
                    } catch (e) {
                        if (e instanceof Error && !(e instanceof SyntaxError)) throw e;
                    }
                }
                throw new Error(`HTTP ${response.status}: ${errorBody}`);
            }

            if (response.status === 204) {
                return null as unknown as T;
            }

            if (options.responseType === 'blob') {
                return (await response.blob()) as unknown as T;
            }

            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return response.json();
            }

            return response.text() as unknown as T;
        } finally {
            clearTimeout(timeoutId);
        }
    }

    private async handleEventStream(response: Response, onProgress?: (data: unknown) => void): Promise<unknown> {
        return new Promise((resolve, reject) => {
            const reader = response.body?.getReader();
            if (!reader) {
                reject(new Error('Não foi possível ler o stream de eventos'));
                return;
            }

            const decoder = new TextDecoder();
            let buffer = '';
            let lastSuccessEvent: unknown = null;
            let lastError: Error | null = null;
            let isStreamClosed = false;

            const processChunk = async () => {
                try {
                    while (!isStreamClosed) {
                        const { done, value } = await reader.read();

                        if (done) {
                            isStreamClosed = true;
                            if (lastSuccessEvent) {
                                resolve(lastSuccessEvent);
                                return;
                            }
                            if (lastError) {
                                reject(lastError);
                                return;
                            }

                            if (response.ok && !lastSuccessEvent && !lastError) {
                                resolve({ event: 'success', message: 'Upload concluído com sucesso' });
                                return;
                            }

                            reject(new Error('Stream finalizado sem eventos de sucesso ou erro'));
                            return;
                        }

                        buffer += decoder.decode(value, { stream: true });
                        const lines = buffer.split('\n\n');
                        buffer = lines.pop() || '';

                        for (const line of lines) {
                            if (line.startsWith('data: ')) {
                                try {
                                    const rawData: unknown = JSON.parse(line.slice(6));
                                    const data = asStreamEventPayload(rawData);

                                    if (onProgress) {
                                        onProgress(rawData);
                                    }

                                    if (data.event === 'success') {
                                        lastSuccessEvent = data.result;
                                        isStreamClosed = true;
                                        resolve(lastSuccessEvent);
                                        return;
                                    } else if (data.event === 'error') {
                                        lastError = new Error(data.message || 'Erro ao processar arquivo');
                                        isStreamClosed = true;
                                        reject(lastError);
                                        return;
                                    }
                                } catch (e) {
                                    logger.debug('Falha ao processar evento SSE:', e);
                                }
                            }
                        }
                    }
                } catch (error) {
                    logger.error('Erro no processamento do stream:', error);
                    isStreamClosed = true;
                    reject(error);
                }
            };

            const cleanup = async () => {
                if (!isStreamClosed) {
                    isStreamClosed = true;
                    try {
                        await reader.cancel();
                    } catch (e) {
                        logger.debug('Falha ao cancelar reader SSE:', e);
                    }
                }
            };

            // Timeout de seguranca para event streams de upload
            const streamTimeout = setTimeout(() => {
                if (!isStreamClosed) {
                    cleanup();
                    reject(new Error('Timeout do stream atingido'));
                }
            }, UPLOAD_STREAM_TIMEOUT_MS);

            processChunk().catch(error => {
                logger.error('Erro fatal no processamento do stream:', error);
                isStreamClosed = true;
                reject(error);
            }).finally(() => {
                clearTimeout(streamTimeout);
            });
        });
    }

    async uploadFile<T>(
        endpoint: string,
        formData: FormData,
        options: RequestOptions = {}
    ): Promise<T> {
        const { requiresAuth = true, isEventStream = false, onProgress, timeout, _isRetry = false, ...restOptions } = options;

        const headers: HeadersInit = {
            'Accept': 'text/event-stream, application/json',
            'Accept-Language': i18n.language || 'pt-BR',
            'Cache-Control': 'no-cache',
            'Connection': 'keep-alive',
            'X-Requested-With': 'XMLHttpRequest'
        };

        if (requiresAuth) {
            const token = await getAuthToken();
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
        }

        let retryCount = 0;
        const maxRetries = 3;
        const baseDelay = 1000; // 1 segundo

        while (retryCount < maxRetries) {
            try {

                const controller = new AbortController();
                const timeoutId = setTimeout(() => {
                    controller.abort();
                }, timeout ?? UPLOAD_TIMEOUT_MS);

                const response = await fetch(`${this.baseUrl}${endpoint}`, {
                    ...restOptions,
                    method: 'POST',
                    headers,
                    body: formData,
                    signal: controller.signal,
                });

                clearTimeout(timeoutId);

                if (response.status === 401 && requiresAuth && !_isRetry) {
                    try {
                        const newToken = await refreshAccessToken();

                        if (newToken) {
                            return this.uploadFile<T>(endpoint, formData, {
                                ...options,
                                _isRetry: true,
                            });
                        } else {
                            await handleTokenRefreshFailure('upload-401-refresh-null');
                            throw new Error(i18n.t('auth:sessionExpired'));
                        }
                    } catch (refreshError) {
                        await handleTokenRefreshFailure('upload-401-refresh-error');
                        throw new Error(i18n.t('auth:sessionExpiredRenew'));
                    }
                }

                if (!response.ok) {
                    const errorBody = await response.text();
                    if (response.status === 403) {
                        try {
                            const parsed: { detail?: { code?: string; message?: string } } = JSON.parse(errorBody);
                            if (parsed?.detail?.code === 'ROLE_NOT_ALLOWED') {
                                sessionStorage.setItem('auth_error', parsed.detail?.message ?? 'Acesso negado.');
                                window.location.href = '/access-denied';
                                throw new Error(parsed.detail?.message ?? 'Acesso negado. Você não possui permissão para acessar este sistema.');
                            }
                        } catch (e) {
                            if (e instanceof Error && !(e instanceof SyntaxError)) throw e;
                        }
                    }
                    throw new Error(`HTTP ${response.status}: ${errorBody}`);
                }

                if (isEventStream) {
                    return this.handleEventStream(response, onProgress) as unknown as T;
                }

                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    return response.json();
                }

                return response.text() as unknown as T;
            } catch (error) {
                retryCount++;
                logger.error(`Erro na tentativa ${retryCount}:`, error);

                if (retryCount === maxRetries) {
                    logger.error(`Falha após ${maxRetries} tentativas:`, error);
                    const message = error instanceof Error ? error.message : String(error);
                    throw new Error(`Erro de rede após ${maxRetries} tentativas: ${message}`);
                }

                const delay = baseDelay * Math.pow(2, retryCount - 1); // Backoff exponencial
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }

        throw new Error('Número máximo de tentativas excedido');
    }

    async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
        return this.request<T>(endpoint, { ...options, method: 'GET' });
    }

    async post<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
        return this.request<T>(endpoint, {
            ...options,
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async put<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
        return this.request<T>(endpoint, {
            ...options,
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async patch<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
        return this.request<T>(endpoint, {
            ...options,
            method: 'PATCH',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
        return this.request<T>(endpoint, { ...options, method: 'DELETE' });
    }
}

export const httpClient = new HttpClient(BASE_URL);
export default httpClient; 